import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChartLine, FaFileInvoiceDollar, FaBed, FaReceipt } from 'react-icons/fa';
import { AuthContext } from '../../authProvider.mjs';
import DelegationInvitations from '../../auditor/components/DelegationInvitations.mjs';
import { HomeCard, CardIcon, CARD_SURFACE, SiteFooter } from '../../components/HomeCard.mjs';
import PageShell from '../../components/PageShell.mjs';
import { useRoleTools } from '../../components/RoleToolCards.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import EnvironmentCard from '../../components/EnvironmentCard.mjs';
import { claimsOpenFor, claimsComingSoonMessage, regionName } from '../../utils/claimRegions.mjs';


const HomePage = () => {
  const navigate = useNavigate();
  const [error, _setError] = useState(null);
  const { isAuthenticated, user } = useContext(AuthContext);
  const { alert } = useDialog();
  // Production takes claims from East Africa only for now. Everyone else keeps
  // the buttons, and picking one says claims are coming to their region.
  const claimsOpen = !isAuthenticated || claimsOpenFor(user);

  // Three cards at most: the person's own claims, filing a claim, and ONE
  // card for their role. Everything else the role can do - reports, user
  // management, PTAE, delegating review - and anything delegated to them is a
  // card on that role's dashboard (RoleToolCards), not here.
  const { dashboard: roleDashboard, delegationNote } = useRoleTools();

  // The band greets the signed-in person by name (useful while testers switch
  // roles and accounts); visitors get the product name instead.
  const bandTitle = isAuthenticated
    ? `Welcome back${user?.firstName ? `, ${user.firstName}` : ''}`
    : 'Stanford Seed Expense Claims';
  const bandSubtitle = isAuthenticated
    ? `Stanford Seed${user?.region ? ` · ${user.region}` : ''}`
    : 'Sign in to file and track your claims';

  // Who an auditor may hand report review to - the same five staff roles the
  // delegation candidate list offers, which are exactly the roles with a
  // dashboard - so nobody sees a prompt they could never receive.
  const canBeDelegate = !!roleDashboard;

  // Single source of truth for where each claim type lives, so the post-login
  // return URL matches the real route. It previously sent users to
  // `/claim/perdiem`, which isn't a route at all - after signing in they landed
  // on a blank page instead of the form they clicked.
  const CLAIM_ROUTES = {
    perdiem: '/perdiem/create',
    expense: '/claim/expense',
    travelcard: '/claim/travel-card',
  };

  const handleClaimClick = (claimType) => {
    const target = CLAIM_ROUTES[claimType] || '/dashboard';
    if (!claimsOpen) {
      alert(claimsComingSoonMessage(user?.region), { title: 'Coming soon' });
      return;
    }
    if (isAuthenticated) {
      navigate(target);
    } else {
      navigate('/login', { state: { returnUrl: target } });
    }
  };

  const handleDashboardClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      // Redirect to login page with return URL
      navigate('/login', { state: { returnUrl: '/dashboard' } });
    }
  };

  // useEffectfor debugging
  // useEffect(() => {
  //     const checkBackendResponse = async () => {
  //         try {
  //             const response = await axios.get(`${API_URL}/auth/status`, {
  //                 withCredentials: true
  //             });

  //         } catch (error) {
  //             console.error("Backend check error:", error);
  //         }
  //     };

  //     if (isAuthenticated) {
  //         checkBackendResponse();
  //     }
  // }, [isAuthenticated]);


  return (
    <>
    {/* The one page whose band stays left-aligned: here it greets the person
        beside their cards; every other page centres its title (PageShell). */}
    <PageShell title={bandTitle} subtitle={bandSubtitle} width="7xl" align="left" centerContent skyline>
        {/* Anything asked of this user before they can work - currently a
            delegation they have been nominated for. Renders nothing when there
            is none, and sits above the cards because it needs an answer.
            Only the staff roles can be delegated to, so an ordinary claimant
            does not make the lookup call at all. */}
        {isAuthenticated && canBeDelegate && (
          <div className="mb-4 sm:mb-6">
            <DelegationInvitations />
          </div>
        )}

        <section className="card-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
          {/* Dashboard Card - Always visible */}
          <HomeCard
            icon={FaChartLine}
            title="User Dashboard"
            onClick={handleDashboardClick}
          >
            <p className="text-sm text-gray-600 sm:text-center">View and manage your expense claims</p>
          </HomeCard>

          {/* File Claim Card - Always visible */}
          <div className={`${CARD_SURFACE} p-5 sm:p-6`}>
            <div className="flex sm:flex-col items-center gap-4 sm:gap-0 mb-4">
              <CardIcon icon={FaFileInvoiceDollar} />
              <div className="sm:text-center">
                <h2 className="text-lg font-bold text-gray-800 sm:mb-1">File a Claim</h2>
                <p className="text-sm text-gray-600">
                  {claimsOpen ? 'Choose a claim type to file' : `Coming soon to ${regionName(user?.region)}`}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center justify-center gap-2 w-full min-h-[44px] py-2.5 px-4 text-base sm:text-sm bg-stanford-red text-white rounded-lg font-medium hover:bg-stanford-light-red transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stanford-red"
                onClick={() => handleClaimClick('perdiem')}
              >
                <FaBed aria-hidden="true" /> Per Diem
              </button>
              <button
                className="flex items-center justify-center gap-2 w-full min-h-[44px] py-2.5 px-4 text-base sm:text-sm bg-stanford-red text-white rounded-lg font-medium hover:bg-stanford-light-red transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-stanford-red"
                onClick={() => handleClaimClick('expense')}
              >
                <FaReceipt aria-hidden="true" /> Expense Reimbursement
              </button>
            </div>
          </div>

          {/* The role card - the one way into everything the role can do, and
              into work delegated to this person. Says when there is delegated
              work, so someone who has just accepted cover can see where it went. */}
          {roleDashboard && (
            <HomeCard
              icon={roleDashboard.icon}
              title={roleDashboard.title}
              onClick={() => navigate(roleDashboard.path)}
            >
              {delegationNote && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-1.5 sm:text-center">
                  {delegationNote}
                </p>
              )}
              <p className="text-sm text-gray-600 sm:text-center">{roleDashboard.blurb}</p>
            </HomeCard>
          )}
      </section>

          {/* Production / Test - admins and above only (and anyone already in
              Test, so they can get back). Claimants never see it. */}
          {isAuthenticated && <EnvironmentCard user={user} className="mt-4 sm:mt-6" />}

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
    </PageShell>

      <SiteFooter />

    </>
  );
};

export default HomePage;
